import { useCallback, useEffect, useRef, useState } from "react";
import { ApiClientError } from "@/lib/api";
import { collectKeys, forgetEntries, onEntryChange, readEntry } from "@/lib/apiCache";

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Tiny data-fetching hook with loading/error state + manual reload.
 *
 * `initialData` seeds the result and SKIPS the initial fetch — used when a parent
 * already has the payload (e.g. the storefront root inlines its home page in the
 * store response), so the child renders immediately instead of round-tripping. A
 * manual `reload()` still fetches.
 *
 * Reads the browser remembers (shared/apiCacheRules.ts) come back without a
 * skeleton, and the hook follows them: when a stale one is refreshed behind the
 * screen, or a save in this or another tab drops it, it fetches again quietly —
 * no loading state, and a failed quiet refresh keeps what is on screen. */
export function useFetch<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
  initialData?: T | null,
): FetchState<T> {
  const seeded = initialData != null;
  const [data, setData] = useState<T | null>(initialData ?? null);
  const [loading, setLoading] = useState(!seeded);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const quiet = useRef(false);
  const followed = useRef<string[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fetcher, deps);

  useEffect(() => {
    // Seeded from a parent payload and not a manual reload → no fetch needed.
    if (seeded && nonce === 0) return;
    let active = true;
    const silent = quiet.current;
    quiet.current = false;
    const { result, keys } = collectKeys(run);
    followed.current = keys;
    const remembered = keys.length > 0 && keys.every((k) => readEntry(k));
    if (!silent && !remembered) {
      setLoading(true);
      setError(null);
    }
    const unfollow = keys.map((k) =>
      onEntryChange(k, () => {
        quiet.current = true;
        setNonce((n) => n + 1);
      }),
    );
    result
      .then((d) => {
        if (!active) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (!active || silent) return;
        setError(e instanceof ApiClientError ? e.message : "Failed to load");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      for (const stop of unfollow) stop();
    };
  }, [run, nonce, seeded]);

  return {
    data,
    loading,
    error,
    reload: () => {
      forgetEntries(followed.current);
      setNonce((n) => n + 1);
    },
  };
}
